/**
 * Influencer discovery — ONE component serving both tickets, because they share one design.
 *
 *   variant="module"  T212648, on the dashboard. Suggestions only, no tabs, a CTA out to
 *                     discovery. Guests are never rendered this: the view component emits
 *                     nothing for a signed-out visitor, since T212648's guest sign-up
 *                     prompt was explicitly waived and the current guest experience stands.
 *
 *   variant="section" T212649, on the profile. "Following" + "Suggested for you" tabs,
 *                     load-more, and an unfollow that removes the row immediately with a
 *                     ~5 second undo.
 *
 * The first page of each list arrives as initial props from the server, so the module
 * paints without a request. Load-more and the not-initially-shown tab go to
 * /umbraco/api/influencers.
 *
 * Follow state lives HERE rather than in the card: an unfollow has to remove a row from a
 * list and stay undoable, which only the list can coordinate.
 */
import { useCallback, useMemo, useState } from "react";
import { InfluencerCard } from "./influencer-card";
import { UndoBar } from "./undo-bar";
import {
  fetchFollowedInfluencers,
  fetchSuggestedInfluencers,
} from "../../../js/api/influencers/influencers";
import {
  useCmsFollowMember,
} from "../../../js/api/follow-member/follow-member.js";
import { SharedIcon } from "../shared/shared-icon";

const TABS = { following: "following", suggested: "suggested" };

/** Normalises a server page into the local list shape. */
const toList = (page) => ({
  influencers: page?.influencers ?? [],
  totalCount: page?.totalCount ?? 0,
  hasMore: page?.hasMore ?? false,
});

export const InfluencerDiscovery = ({
  variant,
  pageSize = 8,
  copy,
  // Defaulted so a cached page rendered before the server started sending `header` degrades to
  // a missing heading rather than a blank module.
  header = {},
  suggested,
  following,
  discoverMoreUrl,
  suggestedTabUrl,
  initialTab = "auto",
}) => {
  const isSection = variant === "section";

  const [suggestedList, setSuggestedList] = useState(() => toList(suggested));
  const [followingList, setFollowingList] = useState(() => toList(following));
  const [pendingUndo, setPendingUndo] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  // An explicit ?influencers= wins, so the empty state's link lands where it says it will.
  // Otherwise the section opens on Following only when there is something there; landing a
  // member on an empty tab when we have suggestions to show would waste the first screen.
  const [activeTab, setActiveTab] = useState(() => {
    if (initialTab === TABS.suggested || initialTab === TABS.following) {
      return initialTab;
    }

    return followingList.influencers.length > 0 ? TABS.following : TABS.suggested;
  });

  const reFollow = useCmsFollowMember();

  const activeList = !isSection || activeTab === TABS.suggested ? suggestedList : followingList;
  const setActiveList = !isSection || activeTab === TABS.suggested ? setSuggestedList : setFollowingList;

  const loadMore = useCallback(async () => {
    setIsLoadingMore(true);
    setError(null);

    const isSuggested = !isSection || activeTab === TABS.suggested;
    const fetcher = isSuggested ? fetchSuggestedInfluencers : fetchFollowedInfluencers;

    try {
      const page = await fetcher({ skip: activeList.influencers.length, take: pageSize });

      setActiveList((current) => ({
        influencers: [...current.influencers, ...(page?.influencers ?? [])],
        totalCount: page?.totalCount ?? current.totalCount,
        hasMore: page?.hasMore ?? false,
      }));
    } catch {
      setError(copy.serverError);
    } finally {
      setIsLoadingMore(false);
    }
  }, [activeList.influencers.length, activeTab, copy.serverError, isSection, pageSize, setActiveList]);

  /**
   * A card reports that its follow state changed. Following-tab unfollows are removed
   * optimistically and held for undo; everything else just flips the flag in place.
   */
  const handleFollowChange = useCallback(
    (influencer, isNowFollowed) => {
      const flip = (list) => ({
        ...list,
        influencers: list.influencers.map((item) =>
          item.memberId === influencer.memberId ? { ...item, isFollowed: isNowFollowed } : item
        ),
      });

      setSuggestedList(flip);

      if (!isSection) {
        return;
      }

      if (isNowFollowed) {
        setFollowingList(flip);
        return;
      }

      // Unfollow from the Following list: drop the row now, remember where it was so Undo
      // can put it back in the same place rather than at the end.
      setFollowingList((list) => {
        const index = list.influencers.findIndex((item) => item.memberId === influencer.memberId);

        if (index === -1) {
          return list;
        }

        setPendingUndo({ influencer: { ...influencer, isFollowed: false }, index });

        return {
          ...list,
          influencers: list.influencers.filter((item) => item.memberId !== influencer.memberId),
          totalCount: Math.max(0, list.totalCount - 1),
        };
      });
    },
    [isSection]
  );

  const handleUndo = useCallback(
    async (pending) => {
      setPendingUndo(null);

      try {
        // The unfollow already reached the server, so undo is a re-follow rather than a
        // cancellation. Restored at its original index.
        await reFollow.mutateAsync({ id: pending.influencer.memberId });

        setFollowingList((list) => {
          const restored = [...list.influencers];
          restored.splice(
            Math.min(pending.index, restored.length),
            0,
            { ...pending.influencer, isFollowed: true }
          );

          return { ...list, influencers: restored, totalCount: list.totalCount + 1 };
        });

        setSuggestedList((list) => ({
          ...list,
          influencers: list.influencers.map((item) =>
            item.memberId === pending.influencer.memberId ? { ...item, isFollowed: true } : item
          ),
        }));
      } catch {
        setError(copy.serverError);
      }
    },
    [copy.serverError, reFollow]
  );

  const cards = useMemo(
    () =>
      activeList.influencers.map((influencer) => (
        <InfluencerCard
          key={influencer.memberId}
          influencer={influencer}
          copy={copy}
          onFollowChange={handleFollowChange}
        />
      )),
    [activeList.influencers, copy, handleFollowChange]
  );

  const isFollowingTab = isSection && activeTab === TABS.following;

  // The chip describes the SUGGESTED list, so it is hidden while Following is open.
  const showChip = !isSection || activeTab === TABS.suggested;

  const emptyMessage = isFollowingTab ? copy.followingEmpty : copy.suggestedEmpty;

  /*
   * T212649 AC4 — the way out of the empty state must be a LINK to a discovery area, not a
   * button that swaps a tab. It was the latter, which meant it could not be opened in a new
   * tab, shared, or used at all before React booted.
   *
   * It is now an anchor with a working href (the server honours ?influencers=suggested on the
   * way back in), and the click is intercepted only to keep the in-page switch instant. An
   * explicit discoverMoreUrl overrides it, so pointing this at a dedicated discovery page
   * later is a prop change rather than a rewrite.
   */
  const emptyCtaHref = discoverMoreUrl ?? suggestedTabUrl;
  const emptyCtaSwitchesTabInPage = !discoverMoreUrl && !!suggestedTabUrl;

  return (
    <section
      className={`influencer-discovery influencer-discovery--${variant}`}
      data-testid="influencer-discovery"
      data-variant={variant}
    >
      <header className="influencer-discovery__header">
        <h2 className="influencer-discovery__title" data-testid="influencer-discovery-title">
          {header.title}
        </h2>

        <p className="influencer-discovery__standfirst">{header.standfirst}</p>
      </header>

      {isSection && (
        <div className="influencer-discovery__tabs" role="tablist" data-testid="influencer-discovery-tabs">
          <button
            type="button"
            role="tab"
            id="influencer-tab-following"
            aria-selected={activeTab === TABS.following}
            aria-controls="influencer-panel"
            className={`influencer-discovery__tab ${
              activeTab === TABS.following ? "influencer-discovery__tab--active" : ""
            }`}
            data-testid="influencer-tab-following"
            onClick={() => setActiveTab(TABS.following)}
          >
            {`${copy.followingTab} (${followingList.totalCount})`}
          </button>

          <button
            type="button"
            role="tab"
            id="influencer-tab-suggested"
            aria-selected={activeTab === TABS.suggested}
            aria-controls="influencer-panel"
            className={`influencer-discovery__tab ${
              activeTab === TABS.suggested ? "influencer-discovery__tab--active" : ""
            }`}
            data-testid="influencer-tab-suggested"
            onClick={() => setActiveTab(TABS.suggested)}
          >
            {copy.suggestedTab}
          </button>
        </div>
      )}

      <UndoBar
        pending={pendingUndo}
        copy={copy}
        onUndo={handleUndo}
        onExpire={() => setPendingUndo(null)}
      />

      <div
        className="influencer-discovery__panel"
        id="influencer-panel"
        role={isSection ? "tabpanel" : undefined}
        aria-labelledby={isSection ? `influencer-tab-${activeTab}` : undefined}
      >
        {cards.length > 0 ? (
          <div className="influencer-discovery__grid" data-testid="influencer-discovery-grid">
            {cards}
          </div>
        ) : (
          /*
            UNDESIGNED empty state (design spec §1.5). The Following copy is verbatim from
            the ticket; the Suggested copy is invented and flagged for review. Deliberately
            encouraging rather than apologetic, and it offers the way out rather than being
            a dead end.
          */
          <div className="influencer-discovery__empty" data-testid="influencer-discovery-empty">
            <p className="influencer-discovery__empty-message">{emptyMessage}</p>

            {isFollowingTab && !!emptyCtaHref && (
              <a
                className="influencer-discovery__empty-cta"
                href={emptyCtaHref}
                data-testid="influencer-discovery-empty-cta"
                onClick={(event) => {
                  if (!emptyCtaSwitchesTabInPage) {
                    return;
                  }

                  // Let modified clicks (new tab, new window, download) behave as the browser
                  // intends — intercepting those is what makes a fake link out of a real one.
                  if (
                    event.defaultPrevented ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey ||
                    event.button !== 0
                  ) {
                    return;
                  }

                  event.preventDefault();
                  setActiveTab(TABS.suggested);
                }}
              >
                {copy.suggestedTab}
              </a>
            )}
          </div>
        )}

        {error && (
          <p className="influencer-discovery__error" role="alert" data-testid="influencer-discovery-error">
            {error}
          </p>
        )}

        {/*
          Load-more belongs to the profile section, which is the surface T212649 requires it
          on. The dashboard module is a fixed-size teaser and sends the member onward to the
          full section instead of growing in place.
        */}
        {isSection && activeList.hasMore && (
          <button
            type="button"
            className="button influencer-discovery__load-more"
            data-testid="influencer-discovery-load-more"
            onClick={loadMore}
            disabled={isLoadingMore}
          >
            {isLoadingMore ? (
              <SharedIcon iconName="#sprite-spinner" className="animate-spin" />
            ) : (
              copy.loadMore
            )}
          </button>
        )}

        {!isSection && discoverMoreUrl && (
          <a
            className="button influencer-discovery__discover-more"
            href={discoverMoreUrl}
            data-testid="influencer-discovery-discover-more"
          >
            {copy.discoverMore}
          </a>
        )}
      </div>
    </section>
  );
};
